// src/features/streams/components/StreamPreview.tsx

import { AspectRatio, Center, Stack, Text, Loader } from '@mantine/core';
import { MediaPlayerLinker } from '@lib2/model/player/PlayerLinker';
import { useResellerBrand } from '@/shared/hooks/useResellerBrand';
import { usePlayers } from '@/features/players/usePlayers';
import { MonetizeUtils } from '@/features/monetize/MonetizeUtils';
import { usePreviewGrant } from '@/features/monetize/usePreviewGrant';
import type { StreamViewData } from '@/lib/types/Stream';
import type IMediaView from '@lib2/model/media/IMediaView';

interface StreamPreviewProps {
  stream: StreamViewData;
}

/**
 * Always renders the player regardless of live status (legacy behavior): the
 * player owns offline presentation via the stream's Offline Display Mode, and
 * live detection can't see HTTP-proxy streams at all — gating on it would blank
 * the preview for streams that are actually on air.
 */
export function StreamPreview({ stream }: StreamPreviewProps) {
  const { defaultPlayerId } = usePlayers();
  const isReseller = useResellerBrand();
  const { grant: previewGrant, ready: previewReady } = usePreviewGrant(`media:${stream.media.mediaId}`);

  if (!defaultPlayerId || !previewReady) {
    return (
      <AspectRatio ratio={16 / 9}>
        <Center bg="gray.1" style={{ borderRadius: 8 }}>
          <Stack align="center" gap="sm">
            <Loader size="lg" />
            <Text c="dimmed">Loading player...</Text>
          </Stack>
        </Center>
      </AspectRatio>
    );
  }

  const mediaView = { data: stream } as unknown as IMediaView;
  const linker = new MediaPlayerLinker(defaultPlayerId, mediaView, isReseller);
  const previewUrl = MonetizeUtils.withPreviewGrant(linker.getPreviewUrl(false, { isPreview: true }), previewGrant);

  return (
    <AspectRatio ratio={16 / 9}>
      <iframe
        src={previewUrl}
        style={{ border: 'none', width: '100%', height: '100%' }}
        allowFullScreen
        title={stream.media.title}
      />
    </AspectRatio>
  );
}
